
import * as Sentry from "@sentry/react";
import posthog from 'posthog-js';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { ClothingProvider } from "./context/ClothingContext";
import { AuthProvider } from "./context/AuthContext";
import { OutfitProvider } from "./context/OutfitContext";
import { AdminViewAsProvider } from "./context/AdminViewAsContext";
import logoOrange from "@/assets/logo-orange.png";

// Static imports — needed on first load
import Landing8 from "./pages/Landing8";
import SanFrancisco from "./pages/SanFrancisco";
import NotFound from "./pages/NotFound";
import RequireAuth from "./components/RequireAuth";
import RouteSeo from "./seo/RouteSeo";
import { CrmEmptyState } from "./components/crm/CrmEmptyState";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index"));
const DigitalCloset = lazy(() => import("./pages/DigitalCloset"));
const ItemDetails = lazy(() => import("./pages/ItemDetails"));
const SavedStyles = lazy(() => import("./pages/SavedStyles"));
const Auth = lazy(() => import("./pages/Auth"));
const VipInvite = lazy(() => import("./pages/VipInvite"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const OutfitDetails = lazy(() => import("./pages/OutfitDetails"));
const StyleLookbook = lazy(() => import("./pages/StyleLookbook"));
const ShoppingRecommendations = lazy(() => import("./pages/ShoppingRecommendations"));
const Checkout = lazy(() => import("./pages/Checkout"));
const ClothingEdits = lazy(() => import("./pages/ClothingEdits"));
const ClothingEditDetails = lazy(() => import("./pages/ClothingEditDetails"));
const FrameUploads = lazy(() => import("./pages/FrameUploads"));
const SharedCloset = lazy(() => import("./pages/SharedCloset"));
const WardrobeGaps = lazy(() => import("./pages/WardrobeGaps"));
const StylistWardrobeAnalysis = lazy(() => import("./pages/StylistWardrobeAnalysis"));
const BlackFriday25 = lazy(() => import("./pages/BlackFriday25"));
const StyleGuide = lazy(() => import("./pages/StyleGuide"));
const UserStyleProfile = lazy(() => import("./pages/UserStyleProfile"));
const ColorAnalysis = lazy(() => import("./pages/ColorAnalysis"));
const ColorAnalysisResults = lazy(() => import("./pages/ColorAnalysisResults"));
const Report = lazy(() => import("./pages/Report"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OnboardingQuizPage = lazy(() => import("./pages/OnboardingQuiz"));
const OnboardingPaymentPage = lazy(() => import("./pages/OnboardingPayment"));
const OnboardingPaymentComplete = lazy(() => import("./pages/OnboardingPaymentComplete"));
const OnboardingBookingPage = lazy(() => import("./pages/OnboardingBooking"));
const OnboardingBodyScanPage = lazy(() => import("./pages/OnboardingBodyScan"));
const OnboardingQuickAddPage = lazy(() => import("./pages/OnboardingQuickAdd"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminImagesRendered = lazy(() => import("./pages/AdminImagesRendered"));
const YouTubeUpload = lazy(() => import("./components/YouTubeUpload"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Stylist = lazy(() => import("./pages/Stylist"));
const StylistApplication = lazy(() => import("./pages/StylistApplication"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const StylistChat = lazy(() => import("./pages/StylistChat"));
const StylistDashboard = lazy(() => import("./pages/StylistDashboard"));
const StylistDashboardDemo = lazy(() => import("./pages/StylistDashboardDemo"));
const StylistChatManager = lazy(() => import("./pages/StylistChatManager"));
const StylistHours = lazy(() => import("./pages/StylistHours"));
const AdminStylistHours = lazy(() => import("./pages/AdminStylistHours"));
const CRM = lazy(() => import("./pages/CRM"));
const CrmClientsView = lazy(() => import("./components/crm/clients/ClientsView"));
const CrmClientDetail = lazy(() => import("./components/crm/clients/ClientDetail"));
const CrmWorkView = lazy(() => import("./components/crm/work/WorkView"));
const CrmPipelineView = lazy(() => import("./components/crm/pipeline/PipelineView"));
const CrmStylistsView = lazy(() => import("./components/crm/stylists/StylistsView"));
const CrmStylistDetail = lazy(() => import("./components/crm/stylists/StylistDetail"));
const CrmFollowUpsView = lazy(() => import("./components/crm/follow-ups/FollowUpsView"));
const OutfitBuilder = lazy(() => import("./pages/OutfitBuilder"));
const OutfitBuilderV2 = lazy(() => import("./pages/OutfitBuilderV2"));
const CreateOutfits = lazy(() => import("./pages/CreateOutfits"));
const CurateShopping = lazy(() => import("./pages/CurateShopping"));
const CatalogBuilder = lazy(() => import("./pages/CatalogBuilder"));
const OrderPrep = lazy(() => import("./pages/OrderPrep"));
const StyleSwaps = lazy(() => import("./pages/StyleSwaps"));
const WritersRoom = lazy(() => import("./pages/WritersRoom"));
const ComposerAIStudio = lazy(() => import("./pages/ComposerAIStudio"));
const Redirecting = lazy(() => import("./pages/Redirecting"));
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const Dashboard1 = lazy(() => import("./pages/Dashboard1"));
const CustomerHelp = lazy(() => import("./pages/CustomerHelp"));
const Dashboard1b = lazy(() => import("./pages/Dashboard1b"));
const BoxCheckout = lazy(() => import("./pages/BoxCheckout"));
const BoxShipping = lazy(() => import("./pages/BoxShipping"));
const Dashboard2 = lazy(() => import("./pages/Dashboard2"));

const Dashboard2b = lazy(() => import("./pages/Dashboard2b"));
const Dashboard3 = lazy(() => import("./pages/Dashboard3"));
const Dashboard4 = lazy(() => import("./pages/Dashboard4"));
const Dashboard5 = lazy(() => import("./pages/Dashboard5"));
const Credits = lazy(() => import("./pages/Credits"));
const Landing1 = lazy(() => import("./pages/Landing1"));
const Landing2 = lazy(() => import("./pages/Landing2"));
const Landing3 = lazy(() => import("./pages/Landing3"));
const Landing4 = lazy(() => import("./pages/Landing4"));
const Landing5 = lazy(() => import("./pages/Landing5"));
const Landing6 = lazy(() => import("./pages/Landing6"));
const Landing7 = lazy(() => import("./pages/Landing7"));
const BodyTypePage = lazy(() => import("./pages/BodyTypePage"));
const StyleHelpPage = lazy(() => import("./pages/StyleHelpPage"));
const WardrobePage = lazy(() => import("./pages/WardrobePage"));
const MenPage = lazy(() => import("./pages/MenPage"));
const LandingLinkedIn = lazy(() => import("./pages/LandingLinkedIn"));
const LandingNewRole = lazy(() => import("./pages/LandingNewRole"));
const LandingOffDuty = lazy(() => import("./pages/LandingOffDuty"));
const LandingRedefineStyle = lazy(() => import("./pages/LandingRedefineStyle"));
const LandingModernComfort = lazy(() => import("./pages/LandingModernComfort"));
const Landing10 = lazy(() => import("./pages/Landing10"));
const Landing11 = lazy(() => import("./pages/Landing11"));
const Landing12 = lazy(() => import("./pages/Landing12"));
const Landing100 = lazy(() => import("./pages/Landing100"));
const Landing20 = lazy(() => import("./pages/Landing20"));
const GiftPurchase = lazy(() => import("./pages/GiftPurchase"));
const GiftConfirmation = lazy(() => import("./pages/GiftConfirmation"));
const Dreamforce = lazy(() => import("./pages/Dreamforce"));
const Promotion = lazy(() => import("./pages/Promotion"));
const Wharton = lazy(() => import("./pages/Wharton"));
const SMS = lazy(() => import("./pages/SMS"));
const SMSTermsAndConditions = lazy(() => import("./pages/SMSTermsAndConditions"));
const SmsSignup = lazy(() => import("./pages/SmsSignup"));
const Mints = lazy(() => import("./pages/Mints"));
const HowToGuide = lazy(() => import("./pages/HowToGuide"));
const Manifesto = lazy(() => import("./pages/Manifesto"));
const Press = lazy(() => import("./pages/Press"));
const Bass = lazy(() => import("./pages/Bass"));
const Chicago = lazy(() => import("./pages/Chicago"));
const AddItemForm = lazy(() => import("./components/AddItemForm"));
const AddItemForm2 = lazy(() => import("./components/AddItemForm2"));
const BulkUploadForm = lazy(() => import("./components/BulkUploadForm"));
const EditItemForm = lazy(() => import("./components/EditItemForm"));
const ProfileEdit = lazy(() => import("./pages/ProfileEdit"));
const Billing = lazy(() => import("./pages/Billing"));
import { ScrollToTop } from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import ThankYouFeedback from "./pages/ThankYouFeedback";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — data stays fresh, no refetch storm on remount
      gcTime: 30 * 60 * 1000, // keep cached results for 30 min
      refetchOnWindowFocus: false, // stop refetching every time the tab regains focus
      retry: 1,
    },
  },
  mutationCache: undefined,
});

// Report unhandled query/mutation errors to Sentry.
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === 'updated' && event.action.type === 'error') {
    Sentry.captureException(event.action.error, { tags: { context: 'react_query' } });
  }
});

// Routes whose path parameter is case-sensitive and must survive canonicalisation.
// An invite token is compared against the database exactly as it arrives, so
// lowercasing the URL turned valid mixed-case invites into "invalid".
const CASE_SENSITIVE_PREFIXES = ["/invite/"];

const NormalizePathname = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (CASE_SENSITIVE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) return;
    if (location.pathname !== location.pathname.toLowerCase()) {
      navigate(location.pathname.toLowerCase() + location.search + location.hash, { replace: true });
    }
  }, [location.pathname]);
  return null;
};

const PostHogPageView = () => {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview', { $current_url: window.location.href });
  }, [location.pathname]);
  return null;
};

// Branded fallback while a lazy route chunk downloads — replaces the blank flash.
// Text-only brand mark (no decorative icon/spinner), gently pulsing.
const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#faf9f5]">
    <img src={logoOrange} alt="Cello" className="h-7 w-auto opacity-80 animate-pulse" />
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <NormalizePathname />
        <PostHogPageView />
        <RouteSeo />
        <ScrollToTop />
        <AuthProvider>
          <AdminViewAsProvider>
            <ClothingProvider>
              <OutfitProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<Landing8 />} />
                      <Route path="/queued" element={<Landing8 variant="queued" />} />
                      <Route path="/mens-styling" element={<Landing8 variant="mens" />} />
                      <Route path="/sanfrancisco" element={<SanFrancisco />} />
                      <Route path="/body-type" element={<BodyTypePage />} />
                      <Route path="/style-help" element={<StyleHelpPage />} />
                      <Route path="/wardrobe" element={<WardrobePage />} />
                      <Route path="/men" element={<MenPage />} />
                      <Route path="/auth" element={<Auth />} />
                      {/* Per-person, expiring invite. */}
                      <Route path="/invite/:token" element={<VipInvite />} />
                      {/* Legacy shared bypass link. Remove after 2026-09-17.
                          No :token param, so VipInvite skips validation and
                          renders the open pre-token signup form. */}
                      <Route path="/invite-8f4b9a2c7e1d" element={<VipInvite />} />
                      {/* Short, printable gift-card link. Resolves to the reusable
                          "pbp2026" code in waitlist_invite_codes; the token route
                          validates and records the claim. Case-insensitive. */}
                      <Route path="/invitepbp2026" element={<Navigate to="/invite/pbp2026" replace />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />

                      <Route path="/youtube-upload" element={<YouTubeUpload />} />
                      <Route path="/onboarding" element={<Onboarding />} />
                      <Route path="/onboarding/quiz" element={<OnboardingQuizPage />} />
                      <Route path="/onboarding/payment" element={<OnboardingPaymentPage />} />
                      <Route path="/onboarding/payment/complete" element={<OnboardingPaymentComplete />} />
                      <Route path="/onboarding/booking" element={<OnboardingBookingPage />} />
                      <Route path="/onboarding/body-scan" element={<OnboardingBodyScanPage />} />
                      <Route path="/onboarding/quickadd" element={<OnboardingQuickAddPage />} />

                      <Route path="/profile/edit" element={<UserStyleProfile />} />

                      {/* Public route for shared closets */}
                      <Route path="/closet/:username" element={<SharedCloset />} />

                      {/* Redirect landing page for OAuth/email callbacks */}
                      <Route path="/redirecting" element={<Redirecting />} />

                      {/* Protected routes that require auth */}
                      <Route element={<RequireAuth />}>
                        <Route path="/coloranalysis" element={<ColorAnalysis />} />
                        <Route path="/coloranalysisresults" element={<ColorAnalysisResults />} />
                        <Route path="/report" element={<Report />} />
                        <Route path="/digital-closet" element={<DigitalCloset />} />
                        <Route path="/frame-uploads" element={<FrameUploads />} />
                        <Route path="/item/:id" element={<ItemDetails />} />
                        <Route path="/add-item" element={<AddItemForm />} />
                        <Route path="/add-item2" element={<AddItemForm2 />} />
                        <Route path="/composerai-studio" element={<ComposerAIStudio />} />
                        <Route path="/bulk-upload" element={<BulkUploadForm />} />
                        <Route path="/edit-item/:id" element={<EditItemForm />} />
                        <Route path="/saved-styles" element={<SavedStyles />} />
                        <Route path="/outfit/:id" element={<OutfitDetails />} />
                        <Route path="/shopping-recommendations" element={<ShoppingRecommendations />} />
                        <Route path="/clothing-edits" element={<ClothingEdits />} />
                        <Route path="/clothing-edit/:id" element={<ClothingEditDetails />} />
                        <Route path="/wardrobe-gaps" element={<WardrobeGaps />} />
                        <Route path="/blackfriday25" element={<BlackFriday25 />} />
                        <Route path="/style-guide" element={<StyleGuide />} />
                        <Route path="/style-guide/:userId" element={<StyleGuide />} />
                        <Route path="/user-style-profile" element={<UserStyleProfile />} />
                        <Route path="/user-style-profile/:userId" element={<UserStyleProfile />} />
                        <Route path="/stylist-analysis" element={<StylistWardrobeAnalysis />} />

                        {/* Dashboard routes */}
                        <Route path="/dashboard" element={<UserDashboard />} />
                        <Route path="/thank-you-feedback" element={<ThankYouFeedback />} />

                        <Route path="/dashboard1" element={<Dashboard1 />} />
                        <Route path="/dashboard1b" element={<Dashboard1b />} />
                        <Route path="/box" element={<BoxCheckout />} />
                        <Route path="/box/shippingconfirmation" element={<BoxShipping />} />
                        <Route path="/help" element={<CustomerHelp />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/dashboard2" element={<Dashboard2 recsRedesign editable />} />
                        <Route path="/dashboard2b" element={<Dashboard2b />} />
                        <Route path="/dashboard3" element={<Dashboard3 />} />
                        <Route path="/dashboard4" element={<Dashboard4 />} />
                        <Route path="/dashboard5" element={<Dashboard5 />} />
                        <Route path="/credits" element={<Credits />} />

                        {/* Admin/Stylist routes */}
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin-dashboard" element={<AdminDashboard />} />
                        <Route path="/stylist-dashboard" element={<StylistDashboard />} />
                        <Route path="/stylist-dashboard-demo" element={<StylistDashboardDemo />} />
                        <Route path="/stylist-chat-manager" element={<StylistChatManager />} />
                        <Route path="/crm" element={<CRM />}>
                          <Route element={<CrmClientsView />}>
                            <Route index element={<CrmEmptyState message="Select a client" />} />
                            <Route path=":customerId" element={<CrmClientDetail />} />
                          </Route>
                          <Route path="active" element={<CrmWorkView />} />
                          <Route path="pipeline" element={<CrmPipelineView />} />
                          <Route path="stylists" element={<CrmStylistsView />}>
                            <Route index element={<CrmEmptyState message="Select a stylist" />} />
                            <Route path=":stylistId" element={<CrmStylistDetail />} />
                          </Route>
                          <Route path="follow-ups" element={<CrmFollowUpsView />} />
                        </Route>
                        <Route path="/imagesrendered" element={<AdminImagesRendered />} />
                        <Route path="/writers-room" element={<WritersRoom />} />
                        <Route path="/outfit-builder" element={<OutfitBuilder />} />
                        <Route path="/outfit-builder-v2" element={<OutfitBuilderV2 />} />
                        <Route path="/create-outfits" element={<CreateOutfits />} />
                        <Route path="/curate-shopping" element={<CurateShopping />} />
                        <Route path="/catalog-builder" element={<CatalogBuilder />} />
                        <Route path="/order-prep" element={<OrderPrep />} />
                        <Route path="/style-swaps" element={<StyleSwaps />} />
                        <Route path="/stylist-chat" element={<StylistChat />} />
                        <Route path="/admin/blog" element={<AdminBlog />} />
                        {/* Hours tracker. Both pages redirect anyone without the
                            role; RLS is the real boundary (see
                            20261005210000_stylist_hours_tracker.sql). */}
                        <Route path="/stylist-hours" element={<StylistHours />} />
                        <Route path="/admin/stylist-hours" element={<AdminStylistHours />} />
                      </Route>

                      <Route path="/style-lookbook" element={<StyleLookbook />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/stylist" element={<Stylist />} />
                      <Route path="/work-with-us" element={<StylistApplication />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<Terms />} />
                      {/* The old policy page: a placeholder with a fake number, replaced by /sms. */}
                      <Route path="/smspolicy" element={<Navigate to="/sms" replace />} />
                      <Route path="/sms" element={<SMS />} />
                      <Route path="/smstermsandconditions" element={<SMSTermsAndConditions />} />
                      <Route path="/sms-signup" element={<SmsSignup />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/landing1" element={<Landing1 />} />
                      <Route path="/landing2" element={<Landing2 />} />
                      <Route path="/landing3" element={<Landing3 />} />
                      <Route path="/landing4" element={<Landing4 />} />
                      <Route path="/landing5" element={<Landing5 />} />
                      <Route path="/landing6" element={<Landing6 />} />
                      <Route path="/landing7" element={<Landing7 />} />
                      <Route path="/landing8" element={<Index />} />
                      <Route path="/linkedin" element={<LandingLinkedIn />} />
                      <Route path="/newrole" element={<LandingNewRole />} />
                      <Route path="/offduty" element={<LandingOffDuty />} />
                      <Route path="/landing10" element={<Landing10 />} />
                      <Route path="/landing11" element={<Landing11 />} />
                      <Route path="/landing12" element={<Landing12 />} />
                      <Route path="/landing100" element={<Landing100 />} />
                      <Route path="/landing20" element={<Landing20 />} />
                      <Route path="/dreamforce" element={<Dreamforce />} />
                      <Route path="/promotion" element={<Promotion />} />
                      <Route path="/wharton" element={<Wharton />} />

                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="/redefine-style" element={<LandingRedefineStyle />} />
                      <Route path="/modern-comfort" element={<LandingModernComfort />} />
                      <Route path="/gift-purchase" element={<GiftPurchase />} />
                      <Route path="/giftconfirmation" element={<GiftConfirmation />} />
                      <Route path="/checkout" element={<Checkout />} />
                      <Route path="/mints" element={<Mints />} />
                      <Route path="/howtoguide" element={<HowToGuide />} />
                      <Route path="/manifesto" element={<Manifesto />} />
                      <Route path="/press" element={<Press />} />
                      <Route path="/bass" element={<Bass />} />
                      <Route path="/chicago" element={<Chicago />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </TooltipProvider>
              </OutfitProvider>
            </ClothingProvider>
          </AdminViewAsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
